import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Shield, ChevronDown, ChevronRight, Check, AlertTriangle, 
  X, Info, RefreshCw, TrendingUp, Lock, Server, Bell,
  FileText, Users, Key, Database, Activity
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { toast } from 'sonner';
import api from '@/lib/api';

const CATEGORY_ICONS = {
  technical: Server,
  administrative: Users,
  physical: Lock,
  breach_notification: Bell
};

const CATEGORY_COLORS = {
  technical: 'from-blue-500 to-blue-600',
  administrative: 'from-purple-500 to-purple-600',
  physical: 'from-green-500 to-green-600',
  breach_notification: 'from-orange-500 to-orange-600'
};

const getScoreColor = (score) => {
  if (score >= 90) return 'text-green-600';
  if (score >= 70) return 'text-blue-600';
  if (score >= 50) return 'text-amber-600';
  return 'text-red-600';
};

const getScoreBg = (score) => {
  if (score >= 90) return 'bg-green-500';
  if (score >= 70) return 'bg-blue-500';
  if (score >= 50) return 'bg-amber-500';
  return 'bg-red-500';
};

const getStatusBadge = (status) => {
  switch (status) {
    case 'compliant':
      return <Badge className="bg-green-500"><Check className="h-3 w-3 mr-1" />Compliant</Badge>;
    case 'partial':
      return <Badge className="bg-amber-500"><AlertTriangle className="h-3 w-3 mr-1" />Partial</Badge>;
    case 'available':
      return <Badge className="bg-blue-500"><Info className="h-3 w-3 mr-1" />Available</Badge>;
    case 'non_compliant':
      return <Badge className="bg-red-500"><X className="h-3 w-3 mr-1" />Non-Compliant</Badge>;
    default:
      return <Badge variant="secondary">{status}</Badge>;
  }
};

function ScoreGauge({ score, size = 'large' }) {
  const circumference = 2 * Math.PI * 45;
  const strokeDashoffset = circumference - (score / 100) * circumference;
  
  const dimensions = size === 'large' ? 'w-48 h-48' : 'w-24 h-24';
  const textSize = size === 'large' ? 'text-4xl' : 'text-xl';
  const labelSize = size === 'large' ? 'text-sm' : 'text-xs';
  
  return (
    <div className={`relative ${dimensions}`}>
      <svg className="w-full h-full transform -rotate-90">
        <circle
          cx="50%"
          cy="50%"
          r="45%"
          fill="none"
          stroke="#e5e7eb"
          strokeWidth="8"
        />
        <circle
          cx="50%"
          cy="50%"
          r="45%"
          fill="none"
          stroke={score >= 90 ? '#22c55e' : score >= 70 ? '#3b82f6' : score >= 50 ? '#f59e0b' : '#ef4444'}
          strokeWidth="8"
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          className="transition-all duration-1000 ease-out"
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className={`font-bold ${textSize} ${getScoreColor(score)}`}>
          {score.toFixed(1)}
        </span>
        <span className={`text-slate-500 ${labelSize}`}>/ 100</span>
      </div>
    </div>
  );
}

function CategoryCard({ categoryId, category, onDrillDown }) {
  const Icon = CATEGORY_ICONS[categoryId] || Shield;
  const colorGradient = CATEGORY_COLORS[categoryId] || 'from-slate-500 to-slate-600';
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ scale: 1.02 }}
      className="cursor-pointer"
      onClick={() => onDrillDown(categoryId)}
    >
      <Card className="border-slate-200 hover:border-slate-300 transition-all">
        <CardContent className="pt-6">
          <div className="flex items-center justify-between mb-4">
            <div className={`p-3 rounded-lg bg-gradient-to-br ${colorGradient}`}>
              <Icon className="h-6 w-6 text-white" />
            </div>
            <ScoreGauge score={category.score} size="small" />
          </div>
          <h3 className="font-semibold text-slate-800 mb-1">{category.name}</h3>
          <p className="text-sm text-slate-500">
            {category.controls?.length || 0} controls evaluated
          </p>
          <div className="mt-3">
            <Progress value={category.score} className="h-2" />
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}

function ControlDetails({ control }) {
  const [isOpen, setIsOpen] = useState(false);
  
  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <CollapsibleTrigger className="w-full">
        <div className="flex items-center justify-between p-3 hover:bg-slate-50 rounded-lg transition-colors">
          <div className="flex items-center gap-3">
            {isOpen ? <ChevronDown className="h-4 w-4 text-slate-400" /> : <ChevronRight className="h-4 w-4 text-slate-400" />}
            <div className="text-left">
              <p className="font-medium text-slate-800">{control.name}</p>
              <p className="text-xs text-slate-500">{control.hipaa_ref}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className={`font-semibold ${getScoreColor(control.score)}`}>
              {control.score.toFixed(1)}%
            </span>
            <Progress value={control.score} className="w-24 h-2" />
          </div>
        </div>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="ml-7 pl-4 border-l-2 border-slate-200 space-y-2 pb-2">
          {control.sub_controls?.map((sub) => (
            <div key={sub.id} className="flex items-center justify-between p-2 bg-slate-50 rounded">
              <div className="flex items-center gap-2">
                {getStatusBadge(sub.status)}
                <span className="text-sm text-slate-700">{sub.name}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className={`text-sm font-medium ${getScoreColor(sub.score)}`}>
                  {sub.score.toFixed(1)}%
                </span>
              </div>
            </div>
          ))}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}

function CategoryDrillDown({ categoryId, category, onBack }) {
  const Icon = CATEGORY_ICONS[categoryId] || Shield;
  
  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
    >
      <div className="flex items-center gap-4 mb-6">
        <Button variant="ghost" size="sm" onClick={onBack}>
          ← Back
        </Button>
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-lg bg-gradient-to-br ${CATEGORY_COLORS[categoryId]}`}>
            <Icon className="h-5 w-5 text-white" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-slate-800">{category.name}</h2>
            <p className="text-sm text-slate-500">
              Score: <span className={`font-semibold ${getScoreColor(category.score)}`}>{category.score.toFixed(1)}%</span>
            </p>
          </div>
        </div>
      </div>
      
      <Card className="border-slate-200">
        <CardContent className="pt-6">
          <div className="space-y-1">
            {category.controls?.map((control) => (
              <ControlDetails key={control.id} control={control} />
            ))}
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}

export default function ComplianceDashboard() {
  const [complianceData, setComplianceData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState(null);

  useEffect(() => {
    fetchComplianceData();
  }, []);

  const fetchComplianceData = async () => {
    setLoading(true);
    try {
      const response = await api.get('/security/compliance/score');
      setComplianceData(response.data);
    } catch (err) {
      console.error('Failed to fetch compliance data:', err);
      toast.error('Failed to load compliance data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <RefreshCw className="h-8 w-8 animate-spin mx-auto text-[#4A238F]" />
          <p className="mt-2 text-slate-500">Calculating compliance score...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="compliance-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <Shield className="h-7 w-7 text-[#4A238F]" />
            HIPAA Compliance Dashboard
          </h1>
          <p className="text-slate-600 mt-1">Real-time security compliance monitoring</p>
        </div>
        <Button onClick={fetchComplianceData} variant="outline" className="gap-2">
          <RefreshCw className="h-4 w-4" />
          Refresh
        </Button>
      </div>

      <AnimatePresence mode="wait">
        {selectedCategory ? (
          <CategoryDrillDown
            key="drilldown"
            categoryId={selectedCategory}
            category={complianceData?.categories[selectedCategory]}
            onBack={() => setSelectedCategory(null)}
          />
        ) : (
          <motion.div
            key="overview"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="space-y-6"
          >
            {/* Overall Score Card */}
            <Card className="border-slate-200 bg-gradient-to-br from-slate-50 to-white">
              <CardContent className="pt-6">
                <div className="flex flex-col lg:flex-row items-center gap-8">
                  <div className="flex-shrink-0">
                    <ScoreGauge score={complianceData?.overall_score || 0} />
                  </div>
                  <div className="flex-1 text-center lg:text-left">
                    <h2 className="text-2xl font-bold text-slate-800 mb-2">
                      Overall HIPAA Compliance Score
                    </h2>
                    <p className="text-slate-600 mb-4">
                      Based on {Object.keys(complianceData?.categories || {}).length} categories and real-time system analysis
                    </p>
                    <div className="flex flex-wrap gap-2 justify-center lg:justify-start">
                      {complianceData?.overall_score >= 90 && (
                        <Badge className="bg-green-500 text-white">
                          <Check className="h-3 w-3 mr-1" /> Excellent
                        </Badge>
                      )}
                      {complianceData?.overall_score >= 70 && complianceData?.overall_score < 90 && (
                        <Badge className="bg-blue-500 text-white">
                          <TrendingUp className="h-3 w-3 mr-1" /> Good
                        </Badge>
                      )}
                      {complianceData?.overall_score < 70 && (
                        <Badge className="bg-amber-500 text-white">
                          <AlertTriangle className="h-3 w-3 mr-1" /> Needs Improvement
                        </Badge>
                      )}
                      <Badge variant="outline" className="text-slate-600">
                        Last updated: {new Date(complianceData?.calculated_at).toLocaleString()}
                      </Badge>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Category Cards */}
            <div>
              <h3 className="text-lg font-semibold text-slate-800 mb-4">Compliance by Category</h3>
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                {Object.entries(complianceData?.categories || {}).map(([categoryId, category]) => (
                  <CategoryCard
                    key={categoryId}
                    categoryId={categoryId}
                    category={category}
                    onDrillDown={setSelectedCategory}
                  />
                ))}
              </div>
            </div>

            {/* Recommendations */}
            {complianceData?.recommendations?.length > 0 && (
              <Card className="border-slate-200">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <TrendingUp className="h-5 w-5 text-[#4A238F]" />
                    Recommendations
                  </CardTitle>
                  <CardDescription>
                    Priority actions to improve your compliance score
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {complianceData.recommendations.map((rec, index) => (
                      <div
                        key={index}
                        className={`p-4 rounded-lg border ${
                          rec.priority === 'high' 
                            ? 'bg-red-50 border-red-200' 
                            : 'bg-amber-50 border-amber-200'
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <Badge variant={rec.priority === 'high' ? 'destructive' : 'secondary'}>
                                {rec.priority.toUpperCase()}
                              </Badge>
                              <span className="font-medium text-slate-800">{rec.control}</span>
                            </div>
                            <p className="text-sm text-slate-600">{rec.issue}</p>
                          </div>
                          <span className={`text-lg font-bold ${getScoreColor(rec.current_score)}`}>
                            {rec.current_score}%
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* HIPAA Reference Quick Links */}
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5 text-[#4A238F]" />
                  HIPAA Security Rule Reference
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-4">
                  <div className="p-3 bg-blue-50 rounded-lg">
                    <p className="font-medium text-blue-800">§164.308</p>
                    <p className="text-sm text-blue-600">Administrative Safeguards</p>
                  </div>
                  <div className="p-3 bg-green-50 rounded-lg">
                    <p className="font-medium text-green-800">§164.310</p>
                    <p className="text-sm text-green-600">Physical Safeguards</p>
                  </div>
                  <div className="p-3 bg-purple-50 rounded-lg">
                    <p className="font-medium text-purple-800">§164.312</p>
                    <p className="text-sm text-purple-600">Technical Safeguards</p>
                  </div>
                  <div className="p-3 bg-orange-50 rounded-lg">
                    <p className="font-medium text-orange-800">§164.400-414</p>
                    <p className="text-sm text-orange-600">Breach Notification</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
